// 2026-05-25 推薦收益紀錄 — 全面改版（截圖一/二 design）
// 結構（不含外卡，由 ReferralTabs 大白卡 wrap）：
// - 4 張統計卡（總/L1/L2/L3 收益）橘色大數字
// - 篩選列 3 欄：開始/結束日期 + 類型 dropdown
// - Level filter underline tabs
// - 2 chart 並列：左 line chart 收益趨勢；右 donut chart 收益類型佔比 + 右側 legend
// - 搜尋框
// - 收益明細 sub-card：header「| 收益明細」+「匯出 Excel 按鈕 + 顯示 dropdown」+ CSS Grid table + footer
import { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  TextField,
  MenuItem,
  Menu,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import dayjs from "dayjs";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import WithdrawalDialog from "./WithdrawalDialog";
import {
  getReferralEarningsSummary,
  getReferralEarningsRecords,
  getWithdrawalEligibility,
} from "../../../../api/userApi";
import { LanguageContext } from "../../../../context/LanguageContext";
import { COLORS } from "theme/tokens";

const fmt1 = (v) => Number(v || 0).toFixed(1);
const fmt2 = (v) => Number(v || 0).toFixed(2);
const fmt0 = (v) => Number(v || 0).toFixed(0);

const fmtDateTime = (v) => (v ? dayjs(v).format("YYYY-MM-DD HH:mm:ss") : "-");

const maskEmail = (email) => {
  if (!email) return "-";
  return `${String(email).slice(0, 5)}*****`;
};

// 用 5 階橘色色階給 donut chart
const PIE_COLORS = ["#ff6b1a", COLORS.main, "#ffa563", "#ffc28a", "#ffd9b8"];

const TYPE_KEY = {
  SUBSCRIPTION_MONTHLY: "TypeSubMonthly",
  SUBSCRIPTION_YEARLY: "TypeSubYearly",
  SUBSCRIPTION_UPGRADE_YEARLY: "TypeUpgradeYearly",
  SUBSCRIPTION_UPGRADE_MONTHLY: "TypeUpgradeMonthly",
  COIN_PURCHASE: "TypeCoinPurchase",
  STRATEGY_RENTAL: "TypeStrategyRental",
  ADDON_SERVICE: "TypeAddonService",
  OTHER: "TypeOther",
};

// value→顯示字。傳入 L(=LANGUAGE.Main) 時走在地化；不傳(如 Excel 匯出) 維持中文。
const typeToZh = (value, L) => {
  const zh = {
    SUBSCRIPTION_MONTHLY: "訂閱（月繳）",
    SUBSCRIPTION_YEARLY: "訂閱（年繳）",
    SUBSCRIPTION_UPGRADE_YEARLY: "升級 → 年繳",
    SUBSCRIPTION_UPGRADE_MONTHLY: "升級 → 月繳",
    COIN_PURCHASE: "儲值 MAS Coin",
    STRATEGY_RENTAL: "策略租賃",
    ADDON_SERVICE: "加值服務",
    OTHER: "其他",
  };
  return (L && L[TYPE_KEY[value]]) || zh[value] || value;
};

const TYPE_OPTIONS = [
  "ALL",
  "SUBSCRIPTION_MONTHLY",
  "SUBSCRIPTION_YEARLY",
  "SUBSCRIPTION_UPGRADE_YEARLY",
  "SUBSCRIPTION_UPGRADE_MONTHLY",
  "COIN_PURCHASE",
  "STRATEGY_RENTAL",
  "ADDON_SERVICE",
  "OTHER",
];

export default function ReferralEarnings() {
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("ReferralTabs", "referral/ReferralTabs/ReferralTabs_lang");
  }, [language]);
  const LANGUAGE = useMemo(
    () => componentLanguageData.ReferralTabs || { Main: {} },
    [componentLanguageData]
  );

  // ===== state =====
  const [summary, setSummary] = useState({
    total: 0,
    byLevel: { LEVEL1: 0, LEVEL2: 0, LEVEL3: 0 },
    trend: [],
    byType: [],
  });
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);

  const [filters, setFilters] = useState({
    keyword: "",
    level: "ALL",
    type: "ALL",
    startDate: "",
    endDate: "",
    page: 0,
    pageSize: 10,
  });

  const [anchorElPageSize, setAnchorElPageSize] = useState(null);

  // 提領：可提領餘額 + 申請對話框
  const [eligibility, setEligibility] = useState(null);
  const [withdrawDialogOpen, setWithdrawDialogOpen] = useState(false);

  // 匯出 Excel Dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    summary: true,
    currentPage: false,
    allRecords: true,
    trend: false,
    byType: false,
  });
  const toggleExportOption = (key) =>
    setExportOptions((p) => ({ ...p, [key]: !p[key] }));

  // ===== fetch =====
  const fetchSummary = async () => {
    try {
      const res = await getReferralEarningsSummary({
        level: filters.level === "ALL" ? undefined : filters.level,
        type: filters.type === "ALL" ? undefined : filters.type,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });

      const levelObj = { LEVEL1: 0, LEVEL2: 0, LEVEL3: 0 };
      (res?.byLevel || []).forEach((lv) => {
        levelObj[lv.level] = lv.amount || 0;
      });

      setSummary({
        total: res?.summary?.total || 0,
        byLevel: levelObj,
        trend: (res?.trend || []).map((t) => ({ name: t.date, amount: t.total })),
        byType: res?.byType || [],
      });
    } catch (err) {
      console.error("取得收益摘要失敗:", err);
    }
  };

  const fetchRecords = async () => {
    try {
      const res = await getReferralEarningsRecords({
        level: filters.level === "ALL" ? undefined : filters.level,
        type: filters.type === "ALL" ? undefined : filters.type,
        keyword: filters.keyword || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        page: filters.page + 1,
        pageSize: filters.pageSize,
      });
      const rowsMapped = (res?.records || []).map((r) => ({
        id: r.id,
        createdAt: r.rawDate,
        level: r.level,
        type: r.type,
        amount: r.amount,
        percentage: r.percentage,
        sourceEmail: r.sourceEmail,
      }));
      setRows(rowsMapped);
      setRowCount(res?.total || rowsMapped.length);
    } catch (err) {
      console.error("取得收益明細失敗:", err);
    }
  };

  const fetchEligibility = async () => {
    try {
      setEligibility(await getWithdrawalEligibility());
    } catch (err) {
      console.error("取得提領資格失敗:", err);
    }
  };

  useEffect(() => {
    fetchEligibility();
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [filters.level, filters.type, filters.startDate, filters.endDate]);

  useEffect(() => {
    fetchRecords();
  }, [
    filters.page,
    filters.pageSize,
    filters.level,
    filters.type,
    filters.keyword,
    filters.startDate,
    filters.endDate,
  ]);

  // ===== handlers =====
  const handleDateChange = (key, value) => {
    const d = value ? dayjs(value).format("YYYY-MM-DD") : "";
    setFilters((f) => ({ ...f, [key]: d, page: 0 }));
  };

  // ===== 匯出 Excel =====
  const handleExportExcel = async () => {
    const { summary: expSum, currentPage, allRecords, trend, byType } = exportOptions;
    if (!expSum && !currentPage && !allRecords && !trend && !byType) {
      window.alert(LANGUAGE.Main.ExportPickOne || "請至少選擇一個匯出項目");
      return;
    }
    try {
      setExporting(true);
      const workbook = XLSX.utils.book_new();

      if (expSum) {
        const summaryRows = [
          { 項目: "累積總收益 (USD)", 金額: fmt1(summary.total) },
          { 項目: "第一層收益 (USD)", 金額: fmt1(summary.byLevel.LEVEL1) },
          { 項目: "第二層收益 (USD)", 金額: fmt1(summary.byLevel.LEVEL2) },
          { 項目: "第三層收益 (USD)", 金額: fmt1(summary.byLevel.LEVEL3) },
        ];
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(summaryRows),
          "Summary"
        );
      }
      if (trend) {
        const trendRows = (summary.trend || []).map((t) => ({
          日期: t.name,
          "總收益 (USD)": fmt1(t.amount),
        }));
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(trendRows), "Trend");
      }
      if (byType) {
        const byTypeRows = (summary.byType || []).map((item) => ({
          類型: typeToZh(item.type),
          "金額 (USD)": fmt1(item.amount),
        }));
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(byTypeRows), "ByType");
      }
      if (currentPage) {
        const cur = rows.map((r) => ({
          時間: fmtDateTime(r.createdAt),
          層級: r.level === "LEVEL1" ? "第一層" : r.level === "LEVEL2" ? "第二層" : "第三層",
          類型: typeToZh(r.type),
          來源用戶: maskEmail(r.sourceEmail),
          "比例 (%)": fmt2(r.percentage),
          "金額 (USD)": fmt1(r.amount),
        }));
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(cur), "CurrentPage");
      }
      if (allRecords) {
        const resAll = await getReferralEarningsRecords({
          level: filters.level === "ALL" ? undefined : filters.level,
          type: filters.type === "ALL" ? undefined : filters.type,
          keyword: filters.keyword || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
          page: 1,
          pageSize: 100000,
        });
        const allRows = (resAll?.records || []).map((r) => ({
          時間: fmtDateTime(r.rawDate),
          層級: r.level === "LEVEL1" ? "第一層" : r.level === "LEVEL2" ? "第二層" : "第三層",
          類型: typeToZh(r.type),
          來源用戶: maskEmail(r.sourceEmail),
          "比例 (%)": fmt2(r.percentage),
          "金額 (USD)": fmt1(r.amount),
        }));
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(allRows), "AllRecords");
      }
      const buf = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buf], { type: "application/octet-stream" }),
        `referral_earnings_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`
      );
      setExportDialogOpen(false);
    } catch (err) {
      console.error("匯出 Excel 失敗:", err);
      window.alert(LANGUAGE.Main.ExportFailed || "匯出 Excel 失敗，請稍後再試");
    } finally {
      setExporting(false);
    }
  };

  // ===== UI =====
  const cardSx = {
    p: 2.5,
    borderRadius: 2,
    border: "1px solid #eef0f3",
    boxShadow: "none",
    height: "100%",
  };

  // donut data
  const byTypeData = (summary.byType || []).map((t, i) => ({
    name: typeToZh(t.type, LANGUAGE.Main),
    value: t.amount,
    color: PIE_COLORS[i % PIE_COLORS.length],
  }));

  // pagination
  const totalPages = Math.max(1, Math.ceil(rowCount / filters.pageSize));
  const currentPage = filters.page + 1;
  const footerText = (LANGUAGE.Main.Footer || "顯示 {{from}}-{{to}} 筆，共 {{total}} 筆資料")
    .replace("{{from}}", rowCount === 0 ? 0 : filters.page * filters.pageSize + 1)
    .replace("{{to}}", Math.min((filters.page + 1) * filters.pageSize, rowCount))
    .replace("{{total}}", rowCount);

  const inputSx = {
    "& .MuiInputBase-root": {
      borderRadius: "8px",
      backgroundColor: COLORS.white,
      fontSize: "14px",
      minHeight: "56px",
    },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.grayLighter },
    "& .MuiInputLabel-root": { fontSize: "14px", color: COLORS.grayDark },
    "& .MuiSelect-select": {
      paddingTop: "16px",
      paddingBottom: "16px",
      display: "flex",
      alignItems: "center",
    },
  };

  // ─── 子元件 ───
  const StatCard = ({ title, value }) => (
    <Card sx={cardSx}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Box sx={{ width: 3, height: 16, background: COLORS.main, borderRadius: 1 }} />
        <MDTypography sx={{ fontSize: "14px", fontWeight: 600, color: COLORS.black }}>{title}</MDTypography>
      </Box>
      <MDTypography sx={{ fontSize: "32px", fontWeight: 700, color: COLORS.main, lineHeight: 1.1 }}>
        ${value}
      </MDTypography>
    </Card>
  );

  return (
    <Box>
      {/* 可提領餘額 + 申請提領 */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2.5,
          mb: 3,
          borderRadius: 2,
          background: "#fff7f1",
          border: "1px solid #ffe2cf",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <MDTypography sx={{ fontSize: "14px", color: COLORS.grayDark }}>
            {LANGUAGE.Main.WdBalanceTitle || "可提領餘額"}
          </MDTypography>
          <MDTypography sx={{ fontSize: "32px", fontWeight: 700, color: COLORS.main, lineHeight: 1.2 }}>
            ${Number(eligibility?.availableUsd || 0).toFixed(2)}
          </MDTypography>
          {Number(eligibility?.minAmount || 0) > 0 && (
            <MDTypography sx={{ fontSize: "12px", color: COLORS.grayMedian }}>
              {(LANGUAGE.Main.WdMinHint || "最低提領金額為 {{min}} USD").replace(
                "{{min}}",
                eligibility?.minAmount
              )}
            </MDTypography>
          )}
          {eligibility &&
            !eligibility.canWithdraw &&
            Number(eligibility.availableUsd || 0) < Number(eligibility.minAmount || 0) && (
              <MDTypography sx={{ fontSize: "12px", color: "#e8833a", fontWeight: 600 }}>
                {(LANGUAGE.Main.WdShortfall ||
                  "目前可提領 {{avail}} USD，尚需 {{need}} USD 才達最低門檻")
                  .replace("{{avail}}", Number(eligibility.availableUsd || 0).toFixed(2))
                  .replace(
                    "{{need}}",
                    (
                      Number(eligibility.minAmount || 0) - Number(eligibility.availableUsd || 0)
                    ).toFixed(2)
                  )}
              </MDTypography>
            )}
        </Box>
        <MDButton
          variant="gradient"
          color="warning"
          disabled={!eligibility?.canWithdraw}
          onClick={() => setWithdrawDialogOpen(true)}
        >
          {LANGUAGE.Main.WdApply || "申請提領"}
        </MDButton>
      </Box>

      {/* 4 統計卡 */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        {/* 修補 M14：改 fmt2(2 位小數)與可提領餘額一致，避免整數四捨五入高估收益 */}
        <StatCard title={LANGUAGE.Main.EarnTotal || "總收益"} value={fmt2(summary.total)} />
        <StatCard title={LANGUAGE.Main.EarnLevel1 || "第一層收益"} value={fmt2(summary.byLevel.LEVEL1)} />
        <StatCard title={LANGUAGE.Main.EarnLevel2 || "第二層收益"} value={fmt2(summary.byLevel.LEVEL2)} />
        <StatCard title={LANGUAGE.Main.EarnLevel3 || "第三層收益"} value={fmt2(summary.byLevel.LEVEL3)} />
      </Box>

      {/* 篩選列：開始日期 / 結束日期 / 類型 */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={LANGUAGE.Main.EarnStartDate || "開始日期"}
            value={filters.startDate ? dayjs(filters.startDate) : null}
            onChange={(v) => handleDateChange("startDate", v)}
            format="YYYY-MM-DD"
            slotProps={{ textField: { fullWidth: true, sx: inputSx } }}
          />
        </LocalizationProvider>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={LANGUAGE.Main.EarnEndDate || "結束日期"}
            value={filters.endDate ? dayjs(filters.endDate) : null}
            onChange={(v) => handleDateChange("endDate", v)}
            format="YYYY-MM-DD"
            slotProps={{ textField: { fullWidth: true, sx: inputSx } }}
          />
        </LocalizationProvider>
        <TextField
          select
          fullWidth
          label={LANGUAGE.Main.EarnType || "類型"}
          value={filters.type}
          onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value, page: 0 }))}
          sx={inputSx}
        >
          {TYPE_OPTIONS.map((value) => (
            <MenuItem key={value} value={value}>
              {value === "ALL"
                ? LANGUAGE.Main.EarnTypeAll || "全部類型"
                : typeToZh(value, LANGUAGE.Main)}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Level filter underline tabs */}
      <Box sx={{ display: "flex", borderBottom: "1px solid #eef0f3", mb: 3 }}>
        {[
          { key: "ALL", label: LANGUAGE.Main.LevelAll || "全部" },
          { key: "LEVEL1", label: LANGUAGE.Main.Level1 || "第一層" },
          { key: "LEVEL2", label: LANGUAGE.Main.Level2 || "第二層" },
          { key: "LEVEL3", label: LANGUAGE.Main.Level3 || "第三層" },
        ].map((t) => {
          const active = filters.level === t.key;
          return (
            <Box
              key={t.key}
              onClick={() => setFilters((f) => ({ ...f, level: t.key, page: 0 }))}
              sx={{
                flex: 1,
                textAlign: "center",
                py: 2,
                cursor: "pointer",
                fontSize: "15px",
                fontWeight: active ? 700 : 500,
                color: active ? COLORS.black : COLORS.grayDark,
                borderBottom: active ? "3px solid #ff8a3d" : "3px solid transparent",
                transition: "all 0.2s",
                marginBottom: "-1px",
                "&:hover": { color: COLORS.black },
              }}
            >
              {t.label}
            </Box>
          );
        })}
      </Box>

      {/* 2 charts 並列 */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          mb: 3,
        }}
      >
        {/* 左：收益趨勢 line chart */}
        <Card sx={cardSx}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Box sx={{ width: 3, height: 16, background: COLORS.main, borderRadius: 1 }} />
            <MDTypography sx={{ fontSize: "14px", fontWeight: 600, color: COLORS.black }}>
              {LANGUAGE.Main.EarnChartTrend || "收益趨勢"}
            </MDTypography>
          </Box>
          <Box sx={{ height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={summary.trend || []}>
                <CartesianGrid strokeDasharray="4 4" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="name"
                  tick={{ fontSize: 12, fill: COLORS.grayDark }}
                  axisLine={{ stroke: COLORS.grayLighter }}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: COLORS.grayDark }}
                  axisLine={{ stroke: COLORS.grayLighter }}
                  tickLine={false}
                  tickFormatter={(v) => fmt0(v)}
                />
                <Tooltip
                  formatter={(v) => `$${fmt2(v)}`}
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #e5e7eb",
                    fontSize: 12,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke={COLORS.main}
                  strokeWidth={2.5}
                  dot={false}
                  isAnimationActive
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </Card>

        {/* 右：收益類型佔比 donut chart + 右側 legend */}
        <Card sx={cardSx}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <Box sx={{ width: 3, height: 16, background: COLORS.main, borderRadius: 1 }} />
            <MDTypography sx={{ fontSize: "14px", fontWeight: 600, color: COLORS.black }}>
              {LANGUAGE.Main.EarnChartType || "收益類型佔比"}
            </MDTypography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, alignItems: "center" }}>
            {/* donut */}
            <Box sx={{ height: 220, position: "relative" }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={byTypeData.length > 0 ? byTypeData : [{ name: "—", value: 1, color: COLORS.grayLighter }]}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={85}
                    startAngle={90}
                    endAngle={-270}
                    stroke="none"
                  >
                    {(byTypeData.length > 0 ? byTypeData : [{ color: COLORS.grayLighter }]).map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              {/* 中央 label */}
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  textAlign: "center",
                  pointerEvents: "none",
                }}
              >
                <MDTypography sx={{ fontSize: "20px", fontWeight: 700, color: COLORS.black }}>
                  {fmt2(summary.total)}
                </MDTypography>
                <MDTypography sx={{ fontSize: "11px", color: COLORS.grayDark }}>USD</MDTypography>
              </Box>
            </Box>
            {/* 右側 legend */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {(byTypeData.length > 0
                ? byTypeData
                : [{ name: "—", value: 0, color: COLORS.grayLighter }]
              ).slice(0, 5).map((d, i) => (
                <Box key={i} display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: d.color }} />
                    <MDTypography sx={{ fontSize: "12px", color: COLORS.grayDark }}>{d.name}</MDTypography>
                  </Box>
                  <MDTypography sx={{ fontSize: "12px", color: COLORS.grayDark, fontWeight: 500 }}>
                    {fmt2(d.value)} USD
                  </MDTypography>
                </Box>
              ))}
            </Box>
          </Box>
        </Card>
      </Box>

      {/* 搜尋框 */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder={LANGUAGE.Main.SearchPlaceholder || "搜尋關鍵字..."}
          value={filters.keyword}
          onChange={(e) => setFilters((f) => ({ ...f, keyword: e.target.value, page: 0 }))}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    background: COLORS.grayLightest,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    "&:hover": { background: COLORS.grayLighter },
                  }}
                >
                  <SearchIcon sx={{ fontSize: 20, color: COLORS.grayDark }} />
                </Box>
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "10px",
              backgroundColor: COLORS.grayLightest,
              fontSize: "14px",
              pr: "8px",
            },
            "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.grayLighter },
          }}
        />
      </Box>

      {/* 收益明細 sub-card */}
      <Card sx={{ borderRadius: 2, border: "1px solid #eef0f3", boxShadow: "none" }}>
        {/* header bar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
            borderBottom: "1px solid #eef0f3",
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ width: 3, height: 18, background: COLORS.main, borderRadius: 1 }} />
            <MDTypography sx={{ fontSize: "16px", fontWeight: 600, color: COLORS.black }}>
              {LANGUAGE.Main.EarnTableTitle || "收益明細"}
            </MDTypography>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <MDButton
              variant="outlined"
              size="small"
              startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
              onClick={() => setExportDialogOpen(true)}
              sx={{
                borderColor: COLORS.main,
                color: "#ff8a3d !important",
                fontSize: "13px",
                fontWeight: 500,
                textTransform: "none",
                px: 2,
                "&:hover": { borderColor: COLORS.main, backgroundColor: COLORS.lighter },
              }}
            >
              {LANGUAGE.Main.EarnExport || "匯出 Excel"}
            </MDButton>
            <Box display="flex" alignItems="center" gap={1}>
              <MDTypography sx={{ fontSize: "13px", color: COLORS.grayDark }}>
                {LANGUAGE.Main.Show || "顯示"}
              </MDTypography>
              <Box
                onClick={(e) => setAnchorElPageSize(e.currentTarget)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  px: 1.5,
                  height: 32,
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                  cursor: "pointer",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: COLORS.black,
                  background: COLORS.white,
                  "&:hover": { borderColor: COLORS.grayMedian, background: COLORS.grayLightest },
                }}
              >
                <span style={{ lineHeight: 1 }}>{filters.pageSize}</span>
                <KeyboardArrowDownIcon sx={{ fontSize: 16, color: COLORS.grayDark }} />
              </Box>
              <Menu
                anchorEl={anchorElPageSize}
                open={Boolean(anchorElPageSize)}
                onClose={() => setAnchorElPageSize(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 80,
                    borderRadius: "10px",
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1)",
                    "& .MuiMenuItem-root": {
                      fontSize: "13px",
                      py: 1,
                      px: 2,
                      justifyContent: "center",
                      "&.Mui-selected": {
                        background: COLORS.lighter,
                        color: COLORS.main,
                        fontWeight: 600,
                      },
                      "&:hover": { background: COLORS.grayLightest },
                    },
                  },
                }}
              >
                {[5, 10, 20, 50].map((n) => (
                  <MenuItem
                    key={n}
                    selected={filters.pageSize === n}
                    onClick={() => {
                      setFilters((f) => ({ ...f, pageSize: n, page: 0 }));
                      setAnchorElPageSize(null);
                    }}
                  >
                    {n}
                  </MenuItem>
                ))}
              </Menu>
            </Box>
          </Box>
        </Box>

        {/* table - CSS Grid (6 cols) */}
        <Box>
          {/* header row */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "22% 12% 18% 18% 14% 16%",
              px: 3,
              py: 1.5,
              backgroundColor: COLORS.grayLightest,
              borderBottom: "1px solid #f5f5f5",
            }}
          >
            {[
              LANGUAGE.Main.EarnColTime || "時間",
              LANGUAGE.Main.EarnColLevel || "層級",
              LANGUAGE.Main.EarnColType || "類型",
              LANGUAGE.Main.EarnColSource || "來源用戶",
              LANGUAGE.Main.EarnColPercentage || "比例 (%)",
              LANGUAGE.Main.EarnColAmount || "金額 (USD)",
            ].map((col, i) => (
              <Box key={i} sx={{ fontSize: "13px", fontWeight: 600, color: COLORS.grayDark }}>
                {col}
              </Box>
            ))}
          </Box>

          {/* body rows */}
          {rows.length === 0 ? (
            <Box sx={{ py: 4, textAlign: "center", color: COLORS.grayDark, fontSize: "13px" }}>
              {LANGUAGE.Main.Empty || "目前尚無資料"}
            </Box>
          ) : (
            rows.map((row) => {
              const levelLabel =
                row.level === "LEVEL1"
                  ? LANGUAGE.Main.Level1 || "第一層"
                  : row.level === "LEVEL2"
                  ? LANGUAGE.Main.Level2 || "第二層"
                  : LANGUAGE.Main.Level3 || "第三層";
              return (
                <Box
                  key={row.id}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "22% 12% 18% 18% 14% 16%",
                    px: 3,
                    py: 2,
                    borderBottom: "1px solid #f5f5f5",
                    transition: "background 0.15s",
                    "&:hover": { backgroundColor: COLORS.grayLightest },
                  }}
                >
                  <Box sx={{ fontSize: "13px", color: COLORS.black }}>{fmtDateTime(row.createdAt)}</Box>
                  <Box sx={{ fontSize: "13px", color: COLORS.black }}>{levelLabel}</Box>
                  <Box sx={{ fontSize: "13px", color: COLORS.black }}>{typeToZh(row.type, LANGUAGE.Main)}</Box>
                  <Box sx={{ fontSize: "13px", color: COLORS.black }}>{maskEmail(row.sourceEmail)}</Box>
                  <Box sx={{ fontSize: "13px", color: COLORS.black }}>{fmt2(row.percentage)}</Box>
                  <Box sx={{ fontSize: "13px", color: COLORS.black }}>{fmt1(row.amount)}</Box>
                </Box>
              );
            })
          )}
        </Box>

        {/* footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
            borderTop: "1px solid #eef0f3",
          }}
        >
          <MDTypography sx={{ fontSize: "13px", color: COLORS.main, fontWeight: 500 }}>
            {footerText}
          </MDTypography>
          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              size="small"
              disabled={currentPage <= 1}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
              sx={{
                width: 32,
                height: 32,
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                "&.Mui-disabled": { opacity: 0.4 },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 16, color: COLORS.grayDark }} />
            </IconButton>
            <Box
              sx={{
                minWidth: 32,
                height: 32,
                px: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                fontSize: "13px",
                fontWeight: 600,
                color: COLORS.black,
              }}
            >
              {currentPage}
            </Box>
            <IconButton
              size="small"
              disabled={currentPage >= totalPages}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
              sx={{
                width: 32,
                height: 32,
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                "&.Mui-disabled": { opacity: 0.4 },
              }}
            >
              <ArrowForwardIcon sx={{ fontSize: 16, color: COLORS.grayDark }} />
            </IconButton>
          </Box>
        </Box>
      </Card>

      {/* 匯出 Excel Dialog (保留原邏輯) */}
      <Dialog
        open={exportDialogOpen}
        onClose={() => !exporting && setExportDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: 18, pb: 1 }}>{LANGUAGE.Main.ExportTitle || "📤 匯出 Excel"}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            {LANGUAGE.Main.ExportDesc || "請選擇要匯出的內容（會自動建立多個工作表 Sheet）："}
          </Typography>
          {[
            { key: "summary", label: LANGUAGE.Main.ExportOptSummary || "📊 Summary（累積總收益 + 各層收益）" },
            { key: "trend", label: LANGUAGE.Main.ExportOptTrend || "📈 收益趨勢（Trend）" },
            { key: "byType", label: LANGUAGE.Main.ExportOptByType || "🧩 收益類型佔比（ByType）" },
            { key: "currentPage", label: LANGUAGE.Main.ExportOptCurrentPage || "📄 目前頁面明細（依現在的分頁）" },
            { key: "allRecords", label: LANGUAGE.Main.ExportOptAllRecords || "📚 全部明細（依目前篩選，不分頁）" },
          ].map((opt) => (
            <FormControlLabel
              key={opt.key}
              control={
                <Checkbox checked={exportOptions[opt.key]} onChange={() => toggleExportOption(opt.key)} />
              }
              label={opt.label}
            />
          ))}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <MDButton
            variant="outlined"
            color="secondary"
            size="small"
            disabled={exporting}
            onClick={() => setExportDialogOpen(false)}
          >
            {LANGUAGE.Main.WdCancelBtn || "取消"}
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={handleExportExcel}
            disabled={exporting}
          >
            {exporting ? LANGUAGE.Main.Exporting || "匯出中..." : LANGUAGE.Main.ExportStart || "開始匯出"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* 申請提領對話框 */}
      <WithdrawalDialog
        open={withdrawDialogOpen}
        onClose={() => setWithdrawDialogOpen(false)}
        onSuccess={() => {
          setWithdrawDialogOpen(false);
          window.alert(LANGUAGE.Main.WdSuccess || "提領申請已送出，審核中");
          fetchEligibility();
        }}
        eligibility={eligibility}
        L={LANGUAGE.Main}
      />
    </Box>
  );
}
